import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogDescription,
} from "../../../components/ui/Dialog";
import Button from "../../../components/ui/Button";
import { AlertTriangle } from "lucide-react";
import type { BulkOperationFailure } from "../../../api/General/Subscription";

/**
 * 🔴 CO-38 — THE BULK RESULT REPORT.
 *
 * A bulk action over 40 companies that skips 12 used to say "28 updated, 12 failed" in a toast and
 * push the per-company rows to `console.warn`. The server had always returned exactly which company
 * was skipped and exactly why; the console threw that away, so the only way to learn it was DevTools.
 * A count is not a report — this dialog is what makes the partial result actionable.
 *
 * Rows are GROUPED BY REFUSAL CODE because the operator's next move is per-cause, not per-company:
 * every BILLING_MANAGED_SUBSCRIPTION row goes to the Platform Billing console, every
 * REVOKE_CLEAR_NEVER_EXPIRES_FIRST row needs the perpetual grant cleared first.
 */
interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  succeededCount: number;
  failures: BulkOperationFailure[];
}

interface FailureGroup {
  code: string | null;
  reason: string;
  rows: BulkOperationFailure[];
}

export default function BulkFailuresDialog({
  open,
  onOpenChange,
  succeededCount,
  failures,
}: Props) {
  const { t } = useTranslation();

  const groups = useMemo<FailureGroup[]>(() => {
    const byCode = new Map<string, FailureGroup>();
    failures.forEach((f) => {
      // Rows with no code still group — by their prose — so nothing is dropped from the report.
      const key = f.code || f.reason || "UNKNOWN";
      const existing = byCode.get(key);
      if (existing) existing.rows.push(f);
      else byCode.set(key, { code: f.code || null, reason: f.reason, rows: [f] });
    });
    return Array.from(byCode.values());
  }, [failures]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 flex items-center justify-center">
              <AlertTriangle size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle>{t("Bulk action finished with skipped companies")}</DialogTitle>
              <DialogDescription className="mt-1">
                {t("{{ok}} updated · {{bad}} skipped", {
                  ok: succeededCount,
                  bad: failures.length,
                })}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t(
              "Nothing was changed for the companies below. Each one was refused for the reason shown — act on them individually once the cause is resolved.",
            )}
          </p>

          <div className="space-y-4 max-h-[22rem] overflow-y-auto">
            {groups.map((group) => (
              <div
                key={group.code || group.reason}
                className="rounded-md border border-border bg-muted/30"
              >
                <div className="px-3 py-2 border-b border-border">
                  <div className="flex items-center gap-2 flex-wrap">
                    {group.code && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-900/50">
                        {group.code}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {t("{{count}} companies", { count: group.rows.length })}
                    </span>
                  </div>
                  <p className="text-sm text-foreground mt-1.5">{group.reason}</p>
                </div>
                <ul className="divide-y divide-border">
                  {group.rows.map((row) => (
                    <li
                      key={row.companyId}
                      className="px-3 py-2 flex items-center gap-2 text-sm"
                    >
                      <span className="min-w-0 truncate text-foreground">
                        {row.companyName || t("Company")}
                      </span>
                      <span className="ms-auto shrink-0 text-xs text-muted-foreground tabular-nums">
                        #{row.companyId}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>{t("Close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
